/**
 * Ticket Lifecycle v2 — Slice 4B
 *
 * Fire-and-forget recorder for advisor work-product events. Callers
 * MUST NOT await this in a way that gates downstream work — failures
 * are logged to console only and never surface as toasts.
 *
 * Gated by `advisorTimingAnalytics` feature flag. When the flag is off
 * (default in every preset until a future slice graduates it), the
 * recorder is a no-op that returns immediately. This keeps data
 * capture invisible until explicitly enabled.
 *
 * Boundary: this module imports neither scheduling, timer, parking,
 * rollover, nor bay code. It is a thin wrapper over the slice-4B
 * `record-advisor-action` edge function.
 */
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import type {
  AdvisorActionKind,
  AdvisorContactChannel,
} from '@/lib/tickets/lifecycle/types';

export interface RecordAdvisorActionArgs {
  ticketId: string;
  actionKind: AdvisorActionKind;
  channel?: AdvisorContactChannel | null;
  amountCents?: number | null;
  metadata?: Record<string, unknown>;
}

/**
 * Returns a `record` function callers can invoke fire-and-forget.
 * The function returns a `Promise<void>` that resolves regardless of
 * outcome — errors are caught internally and logged via `console.error`.
 * Callers may attach `.catch(...)` for additional logging but must not
 * use the rejection as a control-flow signal.
 */
export function useRecordAdvisorAction() {
  const advisorTimingAnalyticsEnabled = useFeatureFlag('advisorTimingAnalytics');

  return useCallback(
    async (args: RecordAdvisorActionArgs): Promise<void> => {
      if (!advisorTimingAnalyticsEnabled) return;
      try {
        const { error, data } = await supabase.functions.invoke('record-advisor-action', {
          body: {
            ticket_id: args.ticketId,
            action_kind: args.actionKind,
            channel: args.channel ?? null,
            amount_cents: args.amountCents ?? null,
            metadata: args.metadata ?? {},
          },
        });
        if (error) {
          const message =
            (data as { error?: string } | null)?.error ||
            error.message ||
            'record-advisor-action failed';
          console.error('[advisor-action] emit failed', {
            actionKind: args.actionKind,
            ticketId: args.ticketId,
            message,
          });
          return;
        }
        if (!data) {
          console.error(
            '[advisor-action] empty response — is the slice-4B edge function deployed?',
          );
          return;
        }
        const payload = data as { error?: string };
        if (payload.error) {
          console.error('[advisor-action] payload error', payload.error);
        }
      } catch (e) {
        // Fire-and-forget: never throw to the caller.
        console.error('[advisor-action] unexpected error', e);
      }
    },
    [advisorTimingAnalyticsEnabled],
  );
}

// ─── Pure mapping helper ───────────────────────────────────────────────

/**
 * Maps a custody event kind to the corresponding advisor action kind to
 * emit, if any.
 *
 *   - foh_accept    → ticket_accepted        (slice 4B: the moment FOH
 *                                              took the ticket)
 *   - kick_to_boh   → kicked_back_to_tech    (slice 4B: FOH handing
 *                                              back; closes the advisor
 *                                              session for analytics)
 *   - kick_to_foh   → ticket_kicked_to_foh   (FOH Timer Handoff Batch 1:
 *                                              symmetric initiation
 *                                              event so analytics can
 *                                              attribute *who started*
 *                                              the handoff without
 *                                              joining the custody event
 *                                              log; widened via
 *                                              migration 20260608120000)
 *
 * Returns `null` for custody events that have no advisor-action analog.
 * Pure: no I/O, no side effects.
 */
export function advisorActionForCustodyEvent(
  eventKind: string,
): AdvisorActionKind | null {
  switch (eventKind) {
    case 'foh_accept':
      return 'ticket_accepted';
    case 'kick_to_boh':
      return 'kicked_back_to_tech';
    case 'kick_to_foh':
      return 'ticket_kicked_to_foh';
    default:
      return null;
  }
}
